<template>
  <div>
    <tabs
      class="mt-2 mb-2"
      :tab-selected="selectedTab"
      :tabs="tabs"
      :content-loading="areTabsBadgesLoading"
      with-border
      @tabClicked="handleTabClick"
    />

    <traceability-data-table
      v-if="groupedExams.length > 0"
      :grouped-exams="groupedExams"
      :redirect-after-exclude-exam="false"
      @nextPage="handleNextPage"
      @afterExcludeExam="handleAfterExcludeExam"
    />

    <div v-else-if="fetchedOrders" class="empty-search">
      <svg-loader src="/static/images/icon-no-tubes-found.svg" />

      <p>Não encontramos nenhum resultado para sua busca.</p>
    </div>
  </div>
</template>

<script>
import traceabilitySearchService from '@/api/traceabilitySearch';

import TraceabilityDataTable from '@/components/TraceabilityV2/TraceabilityDataTable'
import Tabs from '@/components/common/Tabs';

export default {
  name: 'ProductionTraceabilityExams',
  components: {
    TraceabilityDataTable,
    Tabs,
  },
  props: {
    destination: {
      type: Object,
      required: true
    },
    startDate: {
      type: String
    },
    endDate: {
      type: String
    }
  },
  data() {
    return {
      orders: [],
      selectedTab: 0,
      areTabsBadgesLoading: false,
      tabsBadgesCounters: {
        CADASTRADO: undefined,
        EM_TRANSITO: undefined,
        TRIAGEM: undefined,
        EM_EXECUCAO: undefined,
        FINALIZADO: undefined,
        ATRASADO: undefined,
        CANCELADO: undefined,
        RECONVOCADO: undefined,
      },
      hasNext: false,
      nextCursor: '',
      fetchedOrders: false,
    }
  },
  computed: {
    groupedExams() {
      return this.orders
    },
    totalBadgesCount() {
      const excludedKeys = [
        'AMOSTRA_OBTIDA',
        'CANCELADO_CLIENTE',
        'PENDENCIA_CRIADA',
        'PENDENCIA_RESPONDIDA',
        'EM_TRANSITO_ENTRE_NTO',
        'RECOLETADO',
        'SOLICITADA_RECOLETA',
        'ATRASADO',
        'EM_LIBERACAO',
        'ADIANTADO',
      ]

      return Object.keys(this.tabsBadgesCounters)
        .filter(key => !excludedKeys.includes(key))
        .reduce((count, key) => this.tabsBadgesCounters[key] + count ?? 0, 0)
    },
    tabs() {
      return [
        {
          name: 'TODOS',
          key: 'TODOS',
          total: this.totalBadgesCount ?? 0,
          tooltip: 'Todos os exames',
        },
        {
          name: 'CADASTRADOS',
          key: 'CADASTRADO',
          total: this.tabsBadgesCounters?.CADASTRADO ?? 0,
          tooltip: 'Exames cadastrados',
        },
        {
          name: 'EM TRÂNSITO',
          key: 'EM_TRANSITO',
          total: this.tabsBadgesCounters?.EM_TRANSITO ?? 0,
          tooltip: 'Exames em trânsito',
        },
        {
          name: 'ENTREGUES AO NTO',
          key: 'TRIAGEM',
          total: this.tabsBadgesCounters?.TRIAGEM ?? 0,
          tooltip: 'Exames entregues ao NTO',
        },
        {
          name: 'EM EXECUÇÃO',
          key: 'EM_EXECUCAO',
          total: this.tabsBadgesCounters?.EM_EXECUCAO ?? 0,
          tooltip: 'Exames em execução',
        },
        {
          name: 'LIBERADOS',
          key: 'FINALIZADO',
          total: this.tabsBadgesCounters?.FINALIZADO ?? 0,
          tooltip: 'Exames liberados',
        },
        {
          name: 'ATRASADOS',
          key: 'ATRASADO',
          total: this.tabsBadgesCounters?.ATRASADO ?? 0,
          tooltip: 'Exames em atraso',
        },
        {
          name: 'ADIANTADOS',
          key: 'ADIANTADO',
          total: this.tabsBadgesCounters?.ADIANTADO ?? 0,
          tooltip: 'Exames adiantados',
        },
        {
          name: 'CANCELADOS',
          key: 'CANCELADO',
          total: this.tabsBadgesCounters?.CANCELADO ?? 0,
          tooltip: 'Exames cancelados',
        },
        {
          name: 'RECOLETAS',
          key: 'RECOLETADO',
          total: this.tabsBadgesCounters?.RECONVOCADO ?? 0,
          tooltip: 'Recoleta solicitada',
        },
      ]
    },
    payload() {
      return {
        idDestination: this.destination.idDestination,
        filterType: 'periodo',
        filterValue: `${this.startDate}/${this.endDate}`,
        status: this.tabs[this.selectedTab].key,
        limit: 10,
        nextCursor: ''
      }
    }
  },
  mounted() {
    this.handleFilter(this.payload)
  },
  methods: {
    async fetchGroupedExams(payload) {
      const response = await traceabilitySearchService.getGroupedExams(payload)

      return response.data
    },
    async fetchGroupedExamsCount(payload) {
      const response = await traceabilitySearchService.getGroupedExamsCount(payload)

      return response.data ?? {}
    },
    async handleFilter(payload, accumulate = false) {
      this.areTabsBadgesLoading = true
      this.fetchedOrders = false

      const [ordersData, ordersCount] = await Promise.all([
        this.fetchGroupedExams(payload),
        this.fetchGroupedExamsCount(payload),
      ])

      if (ordersData?.data) {
        this.orders = accumulate ? [...this.orders, ...ordersData.data] : ordersData.data ?? []
        this.nextCursor = ordersData.nextCursor
        this.hasNext = ordersData.hasNext
        this.tabsBadgesCounters = ordersCount
        this.fetchedOrders = true
      }

      this.areTabsBadgesLoading = false
    },
    handleTabClick(index) {
      this.selectedTab = index

      this.handleFilter(this.payload)
    },
    async handleNextPage($state) {
      try {
        if (this.hasNext) {
          await this.handleFilter({
            ...this.payload,
            nextCursor: this.nextCursor,
          }, true)

          $state.loaded()
        } else {
          $state.complete()
        }
      } catch (error) {
        console.error('Error loading next page.', error)

        $state.error()
      }
    },
    handleAfterExcludeExam() {
      this.handleFilter(this.payload)
    }
  }
}
</script>


<style lang="scss" scoped>
.empty-search {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 1em;

  .md-icon {
    width: 50px;
    height: 50px;
    margin: 0;
  }

  p {
    font-family: 'Exo 2', sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    text-align: center;
    color: #7a7a7a;
  }
}
</style>
